import { t } from '@if/core/i18n'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { EOperationStatus } from '@src/types/constants'
import { CustomerPaymentOperation, OperationInformation } from '@src/types/loan/loan-operation'
import {
  AddPayment,
  ECalendarSimulationAction,
  EditPayment,
  EditPaymentsCalendarAction,
  SignedCalendarSimulation,
} from '@src/types/loan/loan-simulation'
import { DateOnly } from 'node_modules/@if/core/dist/date/dateOnly'
import { useCallback, useEffect, useMemo, useReducer, useState, useTransition } from 'react'
import { FieldPath, FieldPathValue, useFieldArray, useForm } from 'react-hook-form'
import {
  editPaymentFieldIsDisabled,
  EditPaymentSchemaType,
  EditPaymentsSchemaType,
  ExistingPayment,
  isExistingPayment,
  mapEditPaymentDefaultValue,
  useEditPaymentsForm,
} from './EditPayments-schema'
import { EEditPaymentsSubAction } from './EditPaymentsDialog'

type AddPaymentWithId = AddPayment & { readonly _id: string }

export enum EEditPaymentChangeType {
  Critical,
  Uncritical,
}

export enum EditPaymentsActionStateActionPayloadType {
  SetDefaultValues = 'SET_DEFAULT_VALUES',
  Update = 'UPDATE',
  UpdateNew = 'UPDATE_NEW',
  Add = 'ADD',
  Delete = 'DELETE',
  DeleteNew = 'DELETE_NEW',
  DeleteAll = 'DELETE_ALL',
  Restore = 'RESTORE',
  UpdateDefaultPayments = 'UPDATE_DEFAULT_PAYMENTS',
}

interface EditPaymentsCalendarActionWithIds extends Omit<EditPaymentsCalendarAction, 'newPayments'> {
  newPayments: AddPaymentWithId[]
}

interface BaseEditPaymentsActionStateActionPayload<T extends EditPaymentsActionStateActionPayloadType> {
  type: T
}

interface SetDefaultValuesEditPaymentsActionPayload
  extends BaseEditPaymentsActionStateActionPayload<EditPaymentsActionStateActionPayloadType.SetDefaultValues> {
  defaultPayments: EditPaymentSchemaType[]
}

interface DeleteEditPaymentsActionPayload
  extends BaseEditPaymentsActionStateActionPayload<EditPaymentsActionStateActionPayloadType.Delete> {
  operationId: number
}

interface DeleteNewEditPaymentsActionPayload
  extends BaseEditPaymentsActionStateActionPayload<EditPaymentsActionStateActionPayloadType.DeleteNew> {
  id: string
}

interface RestoreEditPaymentsActionPayload
  extends BaseEditPaymentsActionStateActionPayload<EditPaymentsActionStateActionPayloadType.Restore> {
  payment: ExistingPayment
}

interface DeleteAllEditPaymentsActionPayload
  extends BaseEditPaymentsActionStateActionPayload<EditPaymentsActionStateActionPayloadType.DeleteAll> {
  existingIds: number[]
}

type AddEditPaymentsActionPayload =
  BaseEditPaymentsActionStateActionPayload<EditPaymentsActionStateActionPayloadType.Add> & {
    payments: EditPaymentSchemaType[]
  }

type UpdateEditPaymentsActionPayload = BaseEditPaymentsActionStateActionPayload<
  EditPaymentsActionStateActionPayloadType.Update | EditPaymentsActionStateActionPayloadType.UpdateNew
> &
  EditPaymentSchemaType

type UpdateDefaultPaymentsActionPayload =
  BaseEditPaymentsActionStateActionPayload<EditPaymentsActionStateActionPayloadType.UpdateDefaultPayments> & {
    defaultPayments: EditPaymentSchemaType[]
  }

export type EditPaymentsActionStateActionPayload =
  | SetDefaultValuesEditPaymentsActionPayload
  | DeleteEditPaymentsActionPayload
  | DeleteNewEditPaymentsActionPayload
  | DeleteAllEditPaymentsActionPayload
  | RestoreEditPaymentsActionPayload
  | AddEditPaymentsActionPayload
  | UpdateEditPaymentsActionPayload
  | UpdateDefaultPaymentsActionPayload

/**
 * Build a clean AddPaymentWithId object from the form payment data removing unnecessary fields
 */
const buildCleanAddPayment = (payment: EditPaymentSchemaType): AddPaymentWithId => ({
  _id: payment.id,
  amount: payment.amount,
  effectiveDate: payment.effectiveDate,
  note: payment.note,
  paymentMethod: payment.paymentMethod,
  sendSmsReminder: payment.sendSmsReminder,
})

const dynamicallyCreateEditPaymentChanges = (
  payment: ExistingPayment,
  defaultPayments: EditPaymentSchemaType[],
): { changes: EditPayment; hasChanges: boolean } => {
  const originalPayment = defaultPayments.find((p) => p.id === payment.id)
  if (!originalPayment) throw new Error(`Original payment not found for id: ${payment.id}`)
  let hasChanges = false
  const changes: EditPayment = { operationId: payment.operationId }
  if (originalPayment.amount !== payment.amount) {
    changes.amount = payment.amount
    hasChanges = true
  }
  if (originalPayment.effectiveDate?.getTime() !== payment.effectiveDate?.getTime()) {
    changes.effectiveDate = payment.effectiveDate
    hasChanges = true
  }
  if (originalPayment.note !== payment.note) {
    changes.note = payment.note
    hasChanges = true
  }
  if (originalPayment.paymentMethod !== payment.paymentMethod) {
    changes.paymentMethod = payment.paymentMethod
    hasChanges = true
  }
  if (originalPayment.sendSmsReminder !== payment.sendSmsReminder) {
    changes.sendSmsReminder = payment.sendSmsReminder
    hasChanges = true
  }
  return { changes, hasChanges }
}

const checkHasEdits = (action: EditPaymentsCalendarActionWithIds) =>
  action.deletedPaymentIds.length > 0 || action.newPayments.length > 0 || action.editedPayments.length > 0

interface EditPaymentsActionState {
  defaultPayments: EditPaymentSchemaType[]
  action: EditPaymentsCalendarActionWithIds
  hasEdits: boolean
  isLoadingDefaultValues: boolean
}

const ActionReducer = (
  state: EditPaymentsActionState,
  action: EditPaymentsActionStateActionPayload,
): EditPaymentsActionState => {
  switch (action.type) {
    case EditPaymentsActionStateActionPayloadType.SetDefaultValues: {
      return {
        ...state,
        defaultPayments: action.defaultPayments,
        isLoadingDefaultValues: false,
      }
    }
    case EditPaymentsActionStateActionPayloadType.Add: {
      return {
        ...state,
        action: {
          ...state.action,
          newPayments: [...state.action.newPayments, ...action.payments.map(buildCleanAddPayment)],
        },
        hasEdits: true,
      }
    }
    case EditPaymentsActionStateActionPayloadType.Delete: {
      if (state.action.editedPayments.find((p) => p.operationId === action.operationId)) {
        // If the payment was edited and then deleted, remove it from editedPayments
        return {
          ...state,
          action: {
            ...state.action,
            editedPayments: state.action.editedPayments.filter((p) => p.operationId !== action.operationId),
            deletedPaymentIds: [...state.action.deletedPaymentIds, action.operationId],
          },
          hasEdits: true,
        }
      }

      return {
        ...state,
        action: {
          ...state.action,
          deletedPaymentIds: [...state.action.deletedPaymentIds, action.operationId],
        },
        hasEdits: true,
      }
    }
    case EditPaymentsActionStateActionPayloadType.DeleteNew: {
      const newState = {
        ...state,
        action: {
          ...state.action,
          newPayments: state.action.newPayments.filter((p) => p._id !== action.id),
        },
      }
      newState.hasEdits = checkHasEdits(newState.action)
      return newState
    }
    case EditPaymentsActionStateActionPayloadType.Restore: {
      const newState = {
        ...state,
        action: {
          ...state.action,
          deletedPaymentIds: state.action.deletedPaymentIds.filter((id) => id !== action.payment.operationId),
        },
      }
      const { changes, hasChanges } = dynamicallyCreateEditPaymentChanges(action.payment, state.defaultPayments)
      if (hasChanges) {
        newState.action.editedPayments.push(changes)
      }
      newState.hasEdits = checkHasEdits(newState.action)
      return newState
    }
    case EditPaymentsActionStateActionPayloadType.DeleteAll: {
      const newState = {
        ...state,
        action: {
          ...state.action,
          newPayments: [],
          editedPayments: [],
          deletedPaymentIds: [...state.action.deletedPaymentIds, ...action.existingIds],
        },
      }
      newState.hasEdits = checkHasEdits(newState.action)
      return newState
    }
    case EditPaymentsActionStateActionPayloadType.UpdateNew: {
      const existingEditIndex = state.action.newPayments.findIndex((p) => p._id === action.id)
      if (existingEditIndex === -1) {
        // Not found so this is the first edit, add new AddPayment
        return {
          ...state,
          action: {
            ...state.action,
            newPayments: [...state.action.newPayments, buildCleanAddPayment(action)],
          },
          hasEdits: true,
        }
      } else {
        // Already edited, so update existing AddPayment
        state.action.newPayments[existingEditIndex] = buildCleanAddPayment(action)
        return {
          ...state,
          action: {
            ...state.action,
            newPayments: [...state.action.newPayments],
          },
          hasEdits: true,
        }
      }
    }
    case EditPaymentsActionStateActionPayloadType.Update: {
      if (!isExistingPayment(action)) throw new Error(`Update action requires an existing payment with operationId`)
      const existingEditIndex = state.action.editedPayments.findIndex((p) => p.operationId === action.operationId)
      if (existingEditIndex === -1) {
        // Not found so this is the first edit, add new EditPayment
        const { changes } = dynamicallyCreateEditPaymentChanges(action, state.defaultPayments)
        return {
          ...state,
          action: {
            ...state.action,
            editedPayments: [...state.action.editedPayments, changes],
          },
          hasEdits: true,
        }
      } else {
        // Already edited, so update existing EditPayment or remove if no changes remain
        const { changes, hasChanges } = dynamicallyCreateEditPaymentChanges(action, state.defaultPayments)
        if (hasChanges) {
          state.action.editedPayments[existingEditIndex] = changes
          return {
            ...state,
            action: {
              ...state.action,
              editedPayments: [...state.action.editedPayments],
            },
            hasEdits: true,
          }
        } else {
          // If no changes remain, remove from editedPayments
          const newState: EditPaymentsActionState = {
            ...state,
            action: {
              ...state.action,
              editedPayments: state.action.editedPayments.filter((p) => p.operationId !== action.operationId),
            },
          }
          newState.hasEdits = checkHasEdits(newState.action)
          return newState
        }
      }
    }
    case EditPaymentsActionStateActionPayloadType.UpdateDefaultPayments: {
      return {
        ...DEFAULT_STATE,
        isLoadingDefaultValues: false,
        defaultPayments: action.defaultPayments,
      }
    }
    default:
      throw new Error(`Unhandled action type: ${(action as { type?: string })?.type}`)
  }
}

interface UseEditPaymentsActionProps {
  id: string
  financingProgramId: string
  initialSignedSimulation: SignedCalendarSimulation
  defaultSubAction?: EEditPaymentsSubAction
  selectedPaymentIds: Set<string>
}

const DEFAULT_STATE: EditPaymentsActionState = {
  action: {
    actionType: ECalendarSimulationAction.EditPayments,
    editedPayments: [],
    newPayments: [],
    deletedPaymentIds: [],
  },
  hasEdits: false,
  isLoadingDefaultValues: true,
  defaultPayments: [],
}

/**
 * This hook manages the edit payments action state
 * It optimizes the action state by only keeping track of changes
 * compared to the default payments.
 */
export const useEditPaymentsAction = ({
  id,
  defaultSubAction,
  selectedPaymentIds,
  initialSignedSimulation,
  financingProgramId,
}: UseEditPaymentsActionProps) => {
  const {
    mutateAsync: computeAction,
    isPending: isPendingCompute,
    error,
  } = useComputeCalendarAction(id, financingProgramId)
  const { showError } = useNotification()
  const [isPendingTransition, startTransition] = useTransition()
  const [lastOnChangeType, setLastOnChangeType] = useState<EEditPaymentChangeType | null>(null)
  const [currentSimulation, setCurrentSimulation] = useState<SignedCalendarSimulation>(initialSignedSimulation)

  const refreshSimulation = useCallback(
    async (action: EditPaymentsCalendarAction) => {
      return await computeAction({ ...initialSignedSimulation, action }).then((sim) => {
        setCurrentSimulation(sim)
        return sim
      })
    },
    [computeAction, initialSignedSimulation],
  )

  const { resolver, defaultValues } = useEditPaymentsForm(
    initialSignedSimulation.simulation,
    selectedPaymentIds,
    defaultSubAction === EEditPaymentsSubAction.DeletePayment,
  )

  const formMethods = useForm<EditPaymentsSchemaType>({
    resolver,
    mode: 'onChange',
    // The mapping to load default values is done asynchronously since it can be heavy
    // to avoid blocking the main thread and keep the UI responsive
    // We use queueMicrotask to yield to the main thread before computing the default values
    defaultValues: () => defaultValues,
  })

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { isValid, isValidating, isLoading },
    trigger,
  } = formMethods

  const {
    fields: payments,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'payments',
    keyName: 'key', // avoid conflict with 'id' field in payment
  })

  const [state, dispatch] = useReducer(ActionReducer, DEFAULT_STATE)

  const isPending = isPendingCompute || isPendingTransition || isLoading || state.isLoadingDefaultValues

  useEffect(() => {
    void defaultValues.then((dv) => {
      dispatch({ type: EditPaymentsActionStateActionPayloadType.SetDefaultValues, defaultPayments: dv.payments })
      if (defaultSubAction === EEditPaymentsSubAction.DeletePayment) {
        // If we are in delete mode, mark all default payments as deleted initially
        const deletedPaymentIds = dv.payments.reduce<number[]>((ids, p) => {
          if (p.isLocked || p.operationId == null) return ids
          ids.push(p.operationId)
          return ids
        }, [])
        if (dv.payments.length > 0 && deletedPaymentIds.length === 0) {
          showError(t('loan.payment.editPayments.errors.allSelectedLocked'))
          return
        }
        dispatch({
          type: EditPaymentsActionStateActionPayloadType.DeleteAll,
          existingIds: deletedPaymentIds,
        })
        setLastOnChangeType(EEditPaymentChangeType.Critical)
        void trigger('payments')
      }
    })
    // Only need to run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAddPayment = useCallback(
    (payments: EditPaymentSchemaType | EditPaymentSchemaType[]) => {
      startTransition(() => {
        const isArray = Array.isArray(payments)
        const paymentList = isArray ? payments : [payments]
        if (paymentList.length === 0) return

        const currentPayments = getValues('payments')
        const combinedPayments = [...currentPayments, ...paymentList]
        combinedPayments.sort((a, b) =>
          a.effectiveDate && b.effectiveDate ? a.effectiveDate.getTime() - b.effectiveDate.getTime() : 0,
        )
        setValue('payments', combinedPayments, { shouldDirty: true, shouldTouch: true, shouldValidate: true })

        dispatch({
          type: EditPaymentsActionStateActionPayloadType.Add,
          payments: paymentList,
        })
        setLastOnChangeType(EEditPaymentChangeType.Critical)
      })
    },
    [getValues, setValue],
  )

  const handleDeletePayment = useCallback(
    (index: number, payment: EditPaymentSchemaType) => {
      if (payment.isLocked) return // Should not happen, but just in case
      if (isExistingPayment(payment)) {
        setValue(`payments.${index}.isDeleted`, true, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false, // No need to validate a deleted payment
        })
        startTransition(() => {
          dispatch({ type: EditPaymentsActionStateActionPayloadType.Delete, operationId: payment.operationId })
          setLastOnChangeType(EEditPaymentChangeType.Critical)
        })
      } else {
        // remove doesn't work in transition for some reason
        remove(index)
        startTransition(() => {
          dispatch({ type: EditPaymentsActionStateActionPayloadType.DeleteNew, id: payment.id })
          setLastOnChangeType(EEditPaymentChangeType.Critical)
        })
      }
    },
    [remove, setValue],
  )

  const handleRestorePayment = useCallback(
    (index: number, payment: EditPaymentSchemaType) => {
      startTransition(() => {
        if (payment.isDeleted && isExistingPayment(payment)) {
          setValue(`payments.${index}.isDeleted`, false, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          })
          dispatch({ type: EditPaymentsActionStateActionPayloadType.Restore, payment })
        }
      })
    },
    [setValue],
  )

  const handleDeleteAll = useCallback(() => {
    const payments = getValues('payments')
    const existingIds: number[] = []
    const indicesToRemove: number[] = []
    payments.forEach((p, index) => {
      if (p.isLocked) return // Skip locked payments
      if (isExistingPayment(p)) {
        existingIds.push(p.operationId)
        setValue(`payments.${index}.isDeleted`, true, { shouldDirty: true, shouldTouch: true, shouldValidate: false })
      } else {
        indicesToRemove.push(index)
      }
    })

    // Remove all at once from the end to avoid messing up indices
    remove(indicesToRemove)

    // setValue and remove don't work in transition for some reason
    // probably a async issue
    startTransition(() => {
      dispatch({ type: EditPaymentsActionStateActionPayloadType.DeleteAll, existingIds })
      setLastOnChangeType(EEditPaymentChangeType.Critical)
    })
  }, [getValues, remove, setValue])

  const onUpdateChange = useCallback(
    (index: number, changeType: EEditPaymentChangeType, sortPayments?: boolean) => {
      startTransition(() => {
        const payments = getValues('payments')
        const payment = payments[index]
        if (isExistingPayment(payment)) dispatch({ type: EditPaymentsActionStateActionPayloadType.Update, ...payment })
        else dispatch({ type: EditPaymentsActionStateActionPayloadType.UpdateNew, ...payment })

        // If the effective date changed, we may need to reorder the payments
        if (sortPayments) {
          const newPosition = payments.findIndex(
            (field, i) => i !== index && field.effectiveDate > payment.effectiveDate,
          )
          if (newPosition !== -1 && newPosition !== index) {
            move(index, newPosition)
          }
        }
        setLastOnChangeType(changeType)
      })
    },
    [getValues, move],
  )

  const onCriticalChange = useCallback(
    (index: number, sortPayments?: boolean) => {
      onUpdateChange(index, EEditPaymentChangeType.Critical, sortPayments)
    },
    [onUpdateChange],
  )

  const onUncriticalChange = useCallback(
    (index: number, sortPayments?: boolean) => {
      onUpdateChange(index, EEditPaymentChangeType.Uncritical, sortPayments)
    },
    [onUpdateChange],
  )

  const setBatchValues = useCallback(
    <TName extends FieldPath<EditPaymentSchemaType>>(
      fieldName: TName,
      value:
        | FieldPathValue<EditPaymentSchemaType, TName>
        | ((
            oldValue: FieldPathValue<EditPaymentSchemaType, TName>,
            isLast: boolean,
          ) => FieldPathValue<EditPaymentSchemaType, TName>),
      changeType: EEditPaymentChangeType,
      fromDate?: DateOnly | null,
    ) => {
      const payments = getValues('payments')
      payments.forEach((payment, index) => {
        if (editPaymentFieldIsDisabled(payment, fieldName)) return // Skip deleted or locked payments
        if (fromDate && payment.effectiveDate && payment.effectiveDate < fromDate) return // Skip payments before fromDate
        type ValueFn = (oldValue: FieldPathValue<EditPaymentSchemaType, TName>, isLast: boolean) => FieldPathValue<EditPaymentSchemaType, TName>
        const newValue =
          typeof value === 'function' ? (value as ValueFn)(payment[fieldName] as FieldPathValue<EditPaymentSchemaType, TName>, index === payments.length - 1) : value
        if (isExistingPayment(payment)) {
          dispatch({ type: EditPaymentsActionStateActionPayloadType.Update, ...payment, [fieldName]: newValue })
        } else {
          dispatch({ type: EditPaymentsActionStateActionPayloadType.UpdateNew, ...payment, [fieldName]: newValue })
        }
        setValue(`payments.${index}.${fieldName}`, newValue as never, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
      })
      setLastOnChangeType(changeType)
    },
    [getValues, setValue],
  )

  const updateDefaultPayments = useCallback((defaultPayments: EditPaymentSchemaType[]) => {
    dispatch({ type: EditPaymentsActionStateActionPayloadType.UpdateDefaultPayments, defaultPayments })
  }, [])

  const resetWithPayments = useCallback(
    (fromDate: DateOnly, numberOfPayments: number) => {
      const payments = initialSignedSimulation.simulation.operationInformations
        .filter(
          (opInfo) =>
            opInfo.isCustomerPaymentOperation() &&
            opInfo.operation.status === EOperationStatus.Planned &&
            opInfo.operation.effectiveDate >= fromDate,
        )
        .sort((a, b) => {
          const dateA = a.operation.effectiveDate
          const dateB = b.operation.effectiveDate
          return dateA.getTime() - dateB.getTime()
        })
        .slice(0, numberOfPayments)
        .map((opInfo) => mapEditPaymentDefaultValue(opInfo as OperationInformation<CustomerPaymentOperation>))

      updateDefaultPayments(payments)
      reset({ payments }, { keepDirty: false, keepTouched: false, keepErrors: false })
    },
    [initialSignedSimulation.simulation.operationInformations, reset, updateDefaultPayments],
  )

  const isProcessing = isValidating || isPending

  useEffect(() => {
    if (state.hasEdits) {
      if (lastOnChangeType === EEditPaymentChangeType.Critical && isValid && !isProcessing) {
        void refreshSimulation(state.action)
        setLastOnChangeType(null) // Reset after triggering refresh
      }
    } else {
      setCurrentSimulation(initialSignedSimulation)
    }

    // ATTENTION: The dependency is intentional to avoid too many recomputations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSimulation, state.hasEdits, state.action, isValid, isValidating, lastOnChangeType])

  const getConfirmedSimulation = useCallback(async () => {
    // No edits → return null so the caller skips onConfirm and dialog close.
    // The confirm button is already disabled when !hasEdits; this is a backstop
    // against races and any future caller that forgets to check hasEdits.
    if (!state.hasEdits) {
      return null
    } else if (isValid && !isProcessing) {
      if (lastOnChangeType === EEditPaymentChangeType.Uncritical) {
        return await refreshSimulation(state.action)
      }
      return currentSimulation
    }
    return null
  }, [
    currentSimulation,
    isProcessing,
    isValid,
    lastOnChangeType,
    refreshSimulation,
    state.hasEdits,
    state.action,
  ])

  return useMemo(
    () => ({
      payments,
      addPayment: handleAddPayment,
      deletePayment: handleDeletePayment,
      deleteAllPayments: handleDeleteAll,
      restorePayment: handleRestorePayment,
      onCriticalChange,
      onUncriticalChange,
      getConfirmedSimulation,
      isPending,
      error,
      currentSimulation,
      setBatchValues,
      formMethods,
      resetWithPayments,
      hasEdits: state.hasEdits,
    }),
    [
      payments,
      handleAddPayment,
      handleDeletePayment,
      handleDeleteAll,
      handleRestorePayment,
      onCriticalChange,
      onUncriticalChange,
      getConfirmedSimulation,
      isPending,
      error,
      currentSimulation,
      setBatchValues,
      formMethods,
      resetWithPayments,
      state.hasEdits,
    ],
  )
}
